import React, { useState, useRef, useEffect } from "react";
import useDebounce from "portal/hooks/useDebounce";
import { QUICK_COMMANDS } from "portal/services/smartCommandEngine";

const AssistantInput = ({ onSubmit, isLoading }) => {
  const [value, setValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debouncedValue = useDebounce(value, 300);
  const inputRef = useRef(null);
  const prevDebouncedRef = useRef("");

  // Auto-focus when component mounts
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 300);
    return () => clearTimeout(timer);
  }, []);

  // Show suggestions when debounced value changes and is non-empty
  useEffect(() => {
    if (debouncedValue && debouncedValue !== prevDebouncedRef.current) {
      setShowSuggestions(true);
    }
    prevDebouncedRef.current = debouncedValue;
  }, [debouncedValue]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!value.trim() || isLoading) return;
    onSubmit(value.trim());
    setValue("");
    setShowSuggestions(false);
  };

  const handleQuickCommand = (query) => {
    if (isLoading) return;
    onSubmit(query);
    setValue("");
    setShowSuggestions(false);
  };

  // Filter suggestions based on typed value
  const filteredSuggestions = debouncedValue
    ? QUICK_COMMANDS.filter((cmd) =>
        cmd.label.toLowerCase().includes(debouncedValue.toLowerCase()) ||
        cmd.query.toLowerCase().includes(debouncedValue.toLowerCase())
      )
    : [];

  return (
    <div className="relative">
      {/* Quick command chips — shown when input is empty */}
      {!value && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {QUICK_COMMANDS.map((cmd, i) => (
            <button
              key={i}
              onClick={() => handleQuickCommand(cmd.query)}
              disabled={isLoading}
              className="px-2.5 py-1 text-[11px] font-medium rounded-badge border border-surface-border text-content-secondary hover:border-brand-500 hover:text-brand-500 hover:bg-brand-500/5 transition-all duration-200 disabled:opacity-40"
            >
              {cmd.label}
            </button>
          ))}
        </div>
      )}

      {/* Autocomplete suggestions */}
      {showSuggestions && filteredSuggestions.length > 0 && value && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-surface-card border border-surface-border rounded-card shadow-elevation-3 overflow-hidden z-10">
          {filteredSuggestions.map((cmd, i) => (
            <button
              key={i}
              onClick={() => handleQuickCommand(cmd.query)}
              className="w-full text-left px-3 py-2 text-xs text-content-secondary hover:bg-surface-hover transition-colors flex items-center gap-2"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="stroke-brand-500" strokeWidth="2">
                <polyline points="9 10 4 15 9 20" />
                <path d="M20 4v7a4 4 0 01-4 4H4" />
              </svg>
              {cmd.label}
            </button>
          ))}
        </div>
      )}

      {/* Input form */}
      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => filteredSuggestions.length > 0 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Ask anything... e.g. 'Revenue last month'"
            disabled={isLoading}
            className="w-full px-3.5 py-2.5 text-sm bg-surface-bg border border-surface-border rounded-card focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 transition-all placeholder:text-content-tertiary disabled:opacity-50"
          />
        </div>
        <button
          type="submit"
          disabled={!value.trim() || isLoading}
          className="w-9 h-9 flex items-center justify-center rounded-card bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-40 disabled:hover:bg-brand-500 transition-all duration-200 flex-shrink-0"
        >
          {isLoading ? (
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="31.4 31.4" strokeLinecap="round" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          )}
        </button>
      </form>
    </div>
  );
};

export default AssistantInput;
